import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  Fade,
  Modal,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
} from "@mui/x-data-grid";
import CustomToolbar from "../../utils/customToolbar";
import { useApiClient } from "../../context/ApiClientContext";
import api from "../../helper/baseApi";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import ConfirmDialog from "../../helper/confirmDialog";
import { useNavigate } from "react-router-dom";
import ErrorAlert from "../../helper/errorAlert";
import ComponentBackdrop from "../../components/backdrop";
import SuccessAlert from "../../helper/successAlert";

const Members = ({ teamMembers, teamID, refreshTeam }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { apiUrls: { mobileApiUrl } } = useApiClient();
  const [rows, setRows] = useState(teamMembers);
  const [showButton, setShowButton] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const navigate = useNavigate();
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [userList, setUserList] = useState([]);

  useEffect(() => {
  setRows(teamMembers);
}, [teamMembers]);

  const handleOpenAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleOpenConfirm = () => {
    console.log('id', selectedIds);
    
    if (selectedIds.length === 0) {
      toast.warning("Please select at least one team to delete.");
      return;
    }
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleShowButton = (ids) => {
    setSelectedIds(ids);
    setShowButton(ids.length > 0);
  };

  const onSelectedCheckbox = (id, rows) => {
    let selectedIds = [];

    if (id.type === 'include') {
      selectedIds = Array.from(id.ids);
    }
    else if (id.type === 'exclude') {
      const excluded = new Set(id.ids);
      selectedIds = rows.filter(row => !excluded.has(row.id)).map(row => row.id);
    }
    handleShowButton(selectedIds);
  }

  const handleDelete = async () => {
    setOpenBackdrop(true);
    try {
       // Map selected row IDs to teamMemberIds
    const teamMemberIds = rows
      .filter(row => selectedIds.includes(row.id))
      .map(row => row.teamMemberId); // <-- use your actual field here

      const apiUrl = `${mobileApiUrl}team/deactivate-team-member`;
      const response = await api.post(apiUrl, { teamMemberIds });
      if (response.status === 200) {
        setTimeout(() => {
          setOpenBackdrop(false);

          const deletedItems = rows.filter(item =>
            selectedIds.includes(item.id)
          );
          const deletedNames = deletedItems.map(item => item.name);

          toast.success(`Deleted: ${deletedNames.join(", ")}`);

          setRows(prev =>
            prev.filter(item => !selectedIds.includes(item.id))
          );
          setSelectedIds([]);
          setShowButton(false);
          setOpenConfirm(false);
        }, 2000);

      } else {
        toast.error("Failed to delete team members. Please try again.");
      }
    } catch (error) {
      setTimeout(() => {
        ErrorAlert(error, navigate);
      }, 2000);
    } finally {
      setTimeout(() => {
        setOpenBackdrop(false);
        setSelectedIds([]);
        setShowButton(false);
      }, 2000);
    }
  };

    useEffect(() => {
    fetchOptionUser();
  }, []);

  const fetchOptionUser = async () => {
    try {
      setOpenBackdrop(true);
      // Fetch roles
      let response = await api.get(`${mobileApiUrl}user/user-employee-list`);
      // Set report to
      setUserList(response.data.data.userEmployeeList || []);
      
      setOpenBackdrop(false);
    } catch (error) {
      ErrorAlert(error, navigate);
      setOpenBackdrop(false);
    }
  };

  const onAddMember = async () => {
    setOpenBackdrop(true);
    try {
      const employeeId = selectedMembers.map(member => member.employeeId);

      const apiUrl = `${mobileApiUrl}team/add-team-member`;
      const response = await api.post(apiUrl, {
        teamId: teamID,
        employeeId: employeeId,
      });
      
      if (response.status === 200) {
        await refreshTeam();

        setTimeout(() => {
          setSelectedMembers([]);
          setShowAddModal(false);

          SuccessAlert(response.data.message);
          setOpenBackdrop(false);
        }, 500); // 500ms delay
      } else {
        toast.error("Failed to add team members. Please try again.");
      }
        } catch (error) {
          ErrorAlert(error, navigate);
          setOpenBackdrop(false);
        } 
      };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 80,
      flex: 1,
    },
    {
      field: "role",
      headerName: "Role",
      width: 150,
      flex: 1,
    },
    {
      field: "department",
      headerName: "Department",
      width: 150,
    }
  ];
  return (
    <Paper
      sx={{
        width: "100%",
        my: 3,
        py: 1,
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", // soft shadow
        border: "1px solid #e0e0e0", // subtle border
      }}
    >

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleDelete}
        title="Delete Team Members"
        message={`Are you sure you want to delete ${selectedIds.length > 1 ? "these team members" : "this team member"
          }?`}
        items={rows
          .filter(item => selectedIds.includes(item.id))
          .map(item => item.name)}
        confirmText="Delete"
        confirmColor="error"
      />

      <DataGrid
        columns={columns}
        rows={rows}
        showToolbar
        slots={{
          toolbar: CustomToolbar
        }}
        pageSizeOptions={[5, 10, { value: 50, label: "50" }]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
          sorting: { sortModel: [{ field: "passRate", sort: 'desc' }] }
        }}
        disableRowSelectionOnClick
        onRowSelectionModelChange={(id) => {
          onSelectedCheckbox(id, rows);
        }
        }
        checkboxSelection
        localeText={{ noRowsLabel: "No records found" }}
        slotProps={{
          toolbar: {
            title: "Team Members",
            showAdd: true,
            onAddClick: handleOpenAddModal,
            showDelete: showButton,
            onDelete: handleOpenConfirm,
          },
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
          disableToolbarButton: {
            csvOptions: "true",
            printOptions: "true",
          },
        }}
      />

      <Modal
        open={showAddModal}
        onClose={handleCloseAddModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={showAddModal} timeout={500}>
          <Box
            sx={{
              bgcolor: "background.paper",
              boxShadow: 24,
              px: 3,
              pb: 3,
              borderRadius: 2,
              overflowY: "auto",
              position: "relative",
              width: { xs: "90%", sm: "70%", md: "40%" },
              maxHeight: "90vh",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mt: 3,
                mb: 2,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Select Team Members
            </Typography>

            <Autocomplete
              multiple
              filterSelectedOptions
              options={userList}               // <-- change to your data source
              getOptionLabel={(option) => option.name}
              value={selectedMembers}             // <-- state
              onChange={(event, value) => setSelectedMembers(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.name} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Team Members"
                  placeholder="Select members"
                  variant="outlined"
                />
              )}
              sx={{ mb: 3 }}
            />

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                // variant="outlined"
                color="inherit"
                onClick={handleCloseAddModal}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onAddMember}
              // disabled={!remark.trim()}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
      <ComponentBackdrop openBackdrop={openBackdrop} />

    </Paper>
  );
};

export default Members;